import { ReactNode, createContext, useContext } from 'react'
import { Form, ToastHelper, ToastType, useModal } from '$components'
import { z } from 'zod'
import { useMutation } from '@tanstack/react-query'
import { supportTicketStoreMutation } from '$api-client/@tanstack/react-query.gen'
import { CreateSupportTicketFormControls } from './controls'
import { CreateSupportTicketFormContent } from './content'

export enum Input {
  Message = 'message',
}

export const createSupportTicketSchema = z.object({
  [Input.Message]: z.string()
    .min(1, { message: 'Message is required.' })
    .max(5000, { message: 'Message must be less than 5000 characters.' }),
})

export type CreateSupportTicketSchema = z.infer<typeof createSupportTicketSchema>

type CreateSupportTicketFormContextType = {
  onCancel: () => void
  isLoading?: boolean
}

const CreateSupportTicketFormContext = createContext<CreateSupportTicketFormContextType | undefined>(undefined)

type CreateSupportTicketFormProps = {
  children?: ReactNode
}

export function CreateSupportTicketForm({ children }: CreateSupportTicketFormProps) {
  const { close } = useModal()

  const { mutate, isPending } = useMutation({
    ...supportTicketStoreMutation(),
    onSuccess: () => {
      ToastHelper.createSuccess(ToastType.SupportTicket)
      close()
    },
    onError: () => ToastHelper.createError(ToastType.SupportTicket),
  })

  const handleSubmit = (values: CreateSupportTicketSchema) => {
    mutate({
      body: {
        message: values[Input.Message],
        page_url: `${window.location.origin}${window.location.pathname}`,
      },
    })
  }

  const context: CreateSupportTicketFormContextType = {
    onCancel: close,
    isLoading: isPending,
  }

  return (
    <CreateSupportTicketFormContext.Provider value={context}>
      <Form<CreateSupportTicketSchema, typeof createSupportTicketSchema>
        schema={createSupportTicketSchema}
        onSubmit={handleSubmit}
      >
        {children}
      </Form>
    </CreateSupportTicketFormContext.Provider>
  )
}

CreateSupportTicketForm.Content = CreateSupportTicketFormContent
CreateSupportTicketForm.Controls = CreateSupportTicketFormControls

export function useCreateSupportTicketForm() {
  const context = useContext(CreateSupportTicketFormContext)

  if (context === undefined) {
    throw new Error('useCreateSupportTicketForm must be used within a CreateSupportTicketFormProvider')
  }

  return context
}
